import {
  FormResponseQuestionType,
  IFormResponseCreate,
  IHQTicketDetailsResponse,
  IMetadataTicketDetailsResponse,
  ITicketCreationSummary,
  ITicketCreationSummaryItem,
  ITicketResidentOverview,
  ITicketResidentOverviewEdge,
  ITicketResidentOverviewHQ,
  ITicketResidentOverviewHQEdge,
  ScoreQuestionType,
  TicketingVersion,
  TicketIssueType,
  TicketStatus,
} from '../../models';

interface ITicketOverviewUpdate {
  activeTickets: ITicketResidentOverviewEdge[];
  archiveTickets: ITicketResidentOverviewEdge[];
}

const activeStatuses = [
  TicketStatus.OPEN,
  TicketStatus.IN_PROGRESS,
  TicketStatus.WAITING_FOR_OTHERS,
];
const archiveStatuses = [TicketStatus.CANCELLED, TicketStatus.CLOSED];

// Precompute valid issue types and any aliases for efficient lookups
const ISSUE_TYPE_SET = new Set<TicketIssueType>(
  Object.values(TicketIssueType) as TicketIssueType[]
);
// map BE ticket issue type DAMAGE to internal ticket issue type
const ISSUE_TYPE_ALIASES = new Map<string, TicketIssueType>([
  ['DAMAGE', TicketIssueType.PROPERTY_DAMAGE],
]);

export const ticketConverter = {
  fromHQDetailsToOverview: (details: IHQTicketDetailsResponse): ITicketResidentOverviewHQ => {
    return {
      id: details.id,
      ticketNr: details.ticketNr,
      title: details.title,
      state: details.state,
      category: details.category,
      created: details.created,
      updated: details.updated,
      titleImage: details.titleImage,
      issueType: details.issueType,
      customerSpecificData: details.customerSpecificData,
      externalId: details?.externalId,
    };
  },
  fromMetadataDetailsToOverview: (
    details: IMetadataTicketDetailsResponse,
    existingTicket?: ITicketResidentOverviewEdge
  ): ITicketResidentOverview => {
    return {
      id: details.id,
      title: details.title,
      state: details.status,
      category: existingTicket?.node ? existingTicket.node.category : null,
      created: details.created,
      updated: details.updated,
      issueType: details.issueType,
      externalId: details?.externalId,
    };
  },

  createUpdatedObject: (
    details: IHQTicketDetailsResponse | IMetadataTicketDetailsResponse,
    type: TicketingVersion,
    existingTicket?: ITicketResidentOverviewEdge | ITicketResidentOverviewHQEdge
  ): ITicketResidentOverview | ITicketResidentOverviewHQ => {
    return type === TicketingVersion.HIERARCHICAL
      ? ticketConverter.fromHQDetailsToOverview(details as IHQTicketDetailsResponse)
      : ticketConverter.fromMetadataDetailsToOverview(
          details as IMetadataTicketDetailsResponse,
          existingTicket as ITicketResidentOverviewEdge
        );
  },
  updateTickets: (
    index: number,
    tickets: ITicketResidentOverviewEdge[] | ITicketResidentOverviewHQEdge[],
    updatedTicketsA: ITicketResidentOverviewEdge[] | ITicketResidentOverviewHQEdge[],
    updatedTicketsB: ITicketResidentOverviewEdge[] | ITicketResidentOverviewHQEdge[],
    type: TicketingVersion,
    nodeToUpdate: IHQTicketDetailsResponse | IMetadataTicketDetailsResponse,
    updatedStatus: TicketStatus
  ) => {
    const currentStatus = tickets[index].node.state;
    let updatedObject;
    if (type === TicketingVersion.HIERARCHICAL) {
      updatedObject = ticketConverter.createUpdatedObject(
        nodeToUpdate,
        type,
        tickets[index]
      ) as ITicketResidentOverviewHQ;
    } else {
      updatedObject = ticketConverter.createUpdatedObject(
        nodeToUpdate,
        type,
        tickets[index]
      ) as ITicketResidentOverview;
    }

    if (activeStatuses.includes(currentStatus) && archiveStatuses.includes(updatedStatus)) {
      updatedTicketsA = updatedTicketsA.splice(index, 1);
      updatedTicketsB.unshift({ cursor: updatedObject.id, node: updatedObject });
    } else {
      updatedTicketsA[index] = {
        ...updatedTicketsA[index],
        node: updatedObject,
      };
    }
  },
  updateTicketInStore: (
    activeTickets: ITicketResidentOverviewEdge[] | ITicketResidentOverviewHQEdge[],
    archiveTickets: ITicketResidentOverviewEdge[] | ITicketResidentOverviewHQEdge[],
    nodeToUpdate: IHQTicketDetailsResponse | IMetadataTicketDetailsResponse,
    type: TicketingVersion
  ): ITicketOverviewUpdate => {
    let updatedActiveObjects;
    let updatedArchiveObjects;
    if (type === TicketingVersion.HIERARCHICAL) {
      updatedActiveObjects = activeTickets as ITicketResidentOverviewHQEdge[];
      updatedArchiveObjects = archiveTickets as ITicketResidentOverviewHQEdge[];
    } else {
      updatedActiveObjects = activeTickets as ITicketResidentOverviewEdge[];
      updatedArchiveObjects = archiveTickets as ITicketResidentOverviewEdge[];
    }

    const activeObjectIndex = activeTickets.findIndex(o => o.node.id === nodeToUpdate.id);
    const archiveObjectIndex = archiveTickets.findIndex(o => o.node.id === nodeToUpdate.id);

    const updatedStatus =
      type === TicketingVersion.HIERARCHICAL
        ? (nodeToUpdate as IHQTicketDetailsResponse).state
        : (nodeToUpdate as IMetadataTicketDetailsResponse).status;

    // existing ticket is active
    if (activeObjectIndex !== -1 && archiveObjectIndex === -1) {
      ticketConverter.updateTickets(
        activeObjectIndex,
        activeTickets,
        updatedActiveObjects,
        updatedArchiveObjects,
        type,
        nodeToUpdate,
        updatedStatus
      );
    }
    // existing ticket is archived
    else if (activeObjectIndex === -1 && archiveObjectIndex !== -1) {
      ticketConverter.updateTickets(
        archiveObjectIndex,
        archiveTickets,
        updatedArchiveObjects,
        updatedActiveObjects,
        type,
        nodeToUpdate,
        updatedStatus
      );
    }
    // there is no existing ticket in store
    else if (activeObjectIndex !== -1 && archiveObjectIndex !== -1) {
      if (activeStatuses.includes(updatedStatus)) {
        const updatedObject = ticketConverter.createUpdatedObject(
          nodeToUpdate,
          type,
          activeTickets[activeObjectIndex]
        );
        updatedActiveObjects.unshift({ cursor: updatedObject.id, node: updatedObject });
      } else if (archiveStatuses.includes(updatedStatus)) {
        const updatedObject = ticketConverter.createUpdatedObject(
          nodeToUpdate,
          type,
          archiveTickets[archiveObjectIndex]
        );
        updatedArchiveObjects.unshift({ cursor: updatedObject.id, node: updatedObject });
      }
    }

    return { activeTickets: updatedActiveObjects, archiveTickets: updatedArchiveObjects };
  },

  convertScoreQuestionTypeToFormResponseQuestionType: (
    response: ITicketCreationSummaryItem
  ): FormResponseQuestionType => {
    switch (response.type) {
      case ScoreQuestionType.SELECTION:
        return FormResponseQuestionType.SELECTION;
      case ScoreQuestionType.MULTISELECT:
        return FormResponseQuestionType.MULTISELECT;
      case ScoreQuestionType.INPUT_TEXT:
        return response.multiline
          ? FormResponseQuestionType.TEXTBOX
          : FormResponseQuestionType.TEXTFIELD;
      case ScoreQuestionType.INPUT_DATE:
        return FormResponseQuestionType.DATE;
      case ScoreQuestionType.INPUT_ATTACHMENTS:
        return FormResponseQuestionType.ATTACHMENTS;
      case ScoreQuestionType.LABEL:
        return FormResponseQuestionType.LABEL;
      case ScoreQuestionType.INPUT_NUMBER:
        return FormResponseQuestionType.NUMBERFIELD;
      default:
        return null;
    }
  },

  formatFormResponses: (ticketSummary: ITicketCreationSummary) => {
    const formResponses: IFormResponseCreate[] = [];
    ticketSummary.detailedResponses?.forEach((res, index) => {
      const responseType = ticketConverter.convertScoreQuestionTypeToFormResponseQuestionType(res);
      if (responseType) {
        const response: IFormResponseCreate = {
          index,
          questionText: res.displaySummary?.questionTitle || '',
          type: responseType,
          responses: [],
          attachments: [],
        };
        if (responseType === FormResponseQuestionType.ATTACHMENTS) {
          const attachment = res.displaySummary?.attachments?.map(item => {
            return {
              name: item.name,
              extension: item?.extension,
              content: item.content,
            };
          });
          if (attachment) {
            response.attachments = attachment;
          }
        } else if (responseType === FormResponseQuestionType.LABEL) {
          // No response needed for LABEL types
        } else {
          let answers: string[] = [];
          if (Array.isArray(res.displaySummary?.answerTitle)) {
            answers = res.displaySummary.answerTitle.map(answer =>
              answer !== null && answer !== undefined ? answer : ''
            );
          } else {
            answers.push(
              res.displaySummary?.answerTitle !== null &&
                res.displaySummary?.answerTitle !== undefined
                ? (res.displaySummary?.answerTitle as string)
                : ''
            );
          }
          response.responses.push(...answers);
        }
        formResponses.push(response);
      }
    });
    return formResponses;
  },

  // for example DAMAGE -> PROPERTY_DAMAGE
  normalizeIssueType: (issueType: string): TicketIssueType | null => {
    if (!issueType) return null;
    if (ISSUE_TYPE_SET.has(issueType as TicketIssueType)) return issueType as TicketIssueType;
    const aliasKey = issueType.trim().toUpperCase();
    return ISSUE_TYPE_ALIASES.get(aliasKey) ?? null;
  },
};
